// Paddle checkout overlay.
//
// With Paddle, POST /api/billing/checkout returns Paddle's checkout URL: this
// app's own address (the "default payment link" set in the Paddle dashboard)
// with ?_ptxn=<transaction id>. The app then opens the overlay here with
// Paddle.js. Stripe needs none of this: its checkout is a page of its own.

const PADDLE_JS = 'https://cdn.paddle.com/paddle/v2/paddle.js';
const STORAGE_KEY = 'paddle_txn';

let scriptPromise = null;
let initialized = false;

/** Take ?_ptxn= off the URL (so a reload doesn't reopen checkout) and keep it
 *  until billing state has loaded. Returns the transaction id, if any. */
export function takePaddleTransaction() {
  try {
    const params = new URLSearchParams(window.location.search);
    const txn = params.get('_ptxn');
    if (!txn) return sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.setItem(STORAGE_KEY, txn);
    params.delete('_ptxn');
    const clean = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
    window.history.replaceState({}, '', clean);
    return txn;
  } catch {
    return null;
  }
}

function loadPaddle() {
  if (window.Paddle) return Promise.resolve(window.Paddle);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = PADDLE_JS;
      s.async = true;
      s.onload = () => resolve(window.Paddle);
      s.onerror = () => { scriptPromise = null; reject(new Error('Could not load Paddle checkout')); };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

/** Open the overlay for a pending transaction. `client` is checkout_client
 *  from GET /api/billing/status: { provider, environment, token }. Returns
 *  false when there is nothing to open. */
export async function openPaddleCheckout(client, { onError } = {}) {
  let txn = null;
  try { txn = sessionStorage.getItem(STORAGE_KEY); } catch { /* storage blocked */ }
  if (!txn || client?.provider !== 'paddle') return false;
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* storage blocked */ }
  if (!client.token) {
    onError?.('Checkout isn\'t set up yet. Please contact support@northai.app.');
    return false;
  }
  try {
    const Paddle = await loadPaddle();
    if (!initialized) {
      if (client.environment === 'sandbox') Paddle.Environment.set('sandbox');
      let completed = false;
      Paddle.Initialize({
        token: client.token,
        eventCallback: (event) => {
          // Same return the app already handles for Stripe (?billing=…): it
          // polls billing status until the webhook has landed.
          if (event?.name === 'checkout.completed') {
            completed = true;
            window.location.assign('/?billing=success');
          } else if (event?.name === 'checkout.closed' && !completed) {
            window.location.assign('/?billing=canceled');
          }
        },
      });
      initialized = true;
    }
    Paddle.Checkout.open({ transactionId: txn });
    return true;
  } catch {
    onError?.('Could not open checkout. Please try again or contact support@northai.app.');
    return false;
  }
}
